import type {
	AxisSelection,
	CoverageExplanation,
	DTOOf,
	FacetBatchResult,
	FacetValue,
	IAxis,
	IAxisValue,
	IAxisValueAvailability,
	IBrand,
	IClassificationBootstrap,
	IProduct,
	IValueMapping,
	MatchMode,
	ObjectRef,
	ObjectTags,
} from '@atlas/types'
import { apiClient } from './api.client'

/** What a merge moved. Counts, not entities: the survivor is re-fetched, not returned. */
export interface ValueMergeResult {
	classifications: number
	mappings: number
}

/** One classification tag: tag `objectId` on `axisCode` with `valueCode`. */
export interface ClassifyTuple {
	objectType: string
	objectId: string
	axisCode: string
	valueCode: string
	/** Which of the object's tuples this tag belongs to. Omitted ⇒ the object's
	 *  default tuple, which is how every existing caller behaves. */
	tupleId?: number
}

/**
 * Classification admin API client. The UI reads the axis registry dynamically —
 * no hard-coded dimension names anywhere (design D5). Every screen derives its
 * columns/forms from these DTOs.
 */

export type AxisDTO = DTOOf<IAxis>
export type AxisValueDTO = DTOOf<IAxisValue>
export type ValueMappingDTO = DTOOf<IValueMapping>
export type AvailabilityDTO = DTOOf<IAxisValueAvailability>
export type BrandDTO = DTOOf<IBrand>
export type ProductDTO = DTOOf<IProduct>
/** Per-axis selection of value codes, keyed by axis code (the accumulated facet filter). */
export type FacetFilter = Record<string, string[]>
export type { FacetValue }

const unwrap = <T>(data: { data: T }): T => data.data

export const classificationApi = {
	// Bootstrap — the whole active axis + value registry in one call (session-cacheable).
	bootstrap: () => apiClient.get<IClassificationBootstrap>('/classification/bootstrap').then((r) => r.data),

	// Axes
	listAxes: () => apiClient.get<{ data: AxisDTO[] }>('/axes').then((r) => unwrap(r.data)),
	createAxis: (body: { code: string; name: string; kind: string; valueType?: string; isHierarchical?: boolean }) =>
		apiClient.post<AxisDTO>('/axes', body).then((r) => r.data),
	updateAxis: (
		axisId: string,
		body: Partial<{ name: string; valueType: string; isHierarchical: boolean; status: string }>,
	) => apiClient.patch<AxisDTO>(`/axes/${axisId}`, body).then((r) => r.data),

	/** Soft-retire: the axis stops being offered and keeps every tag filed against it. */
	retireAxis: (axisId: string) => apiClient.delete<AxisDTO>(`/axes/${axisId}`).then((r) => r.data),

	// Values
	listValues: (axisId: string) =>
		apiClient.get<{ data: AxisValueDTO[] }>(`/axes/${axisId}/values`).then((r) => unwrap(r.data)),
	createValue: (axisId: string, body: { code: string; label: string; parentId?: string }) =>
		apiClient.post<AxisValueDTO>(`/axes/${axisId}/values`, body).then((r) => r.data),
	updateValue: (
		axisId: string,
		valueId: string,
		body: Partial<{ label: string; status: string; meta: Record<string, unknown> }>,
	) => apiClient.patch<AxisValueDTO>(`/axes/${axisId}/values/${valueId}`, body).then((r) => r.data),
	reparentValue: (axisId: string, valueId: string, parentId: string | null) =>
		apiClient.put<AxisValueDTO>(`/axes/${axisId}/values/${valueId}/parent`, { parentId }).then((r) => r.data),
	retireValue: (axisId: string, valueId: string) =>
		apiClient.delete<AxisValueDTO>(`/axes/${axisId}/values/${valueId}`).then((r) => r.data),

	/** Retires `valueId` and moves everything pointing at it onto `into`. Returns what moved. */
	mergeValue: (axisId: string, valueId: string, into: string) =>
		apiClient.post<ValueMergeResult>(`/axes/${axisId}/values/${valueId}/merge`, { into }).then((r) => r.data),

	// Mappings + availability
	listMappings: (valueId: string) =>
		apiClient.get<{ data: ValueMappingDTO[] }>(`/values/${valueId}/mappings`).then((r) => unwrap(r.data)),
	createMapping: (valueId: string, body: { system: string; geoValueId?: string | null; externalCode: string }) =>
		apiClient.post<ValueMappingDTO>(`/values/${valueId}/mappings`, body).then((r) => r.data),
	/** Scope is part of a mapping's identity, so the geography goes in the query. */
	deleteMapping: (valueId: string, system: string, geoValueId: string | null) =>
		apiClient
			.delete<void>(`/values/${valueId}/mappings`, { params: { system, ...(geoValueId ? { geoValueId } : {}) } })
			.then((r) => r.data),
	listAvailability: (valueId: string) =>
		apiClient.get<{ data: AvailabilityDTO[] }>(`/values/${valueId}/availability`).then((r) => unwrap(r.data)),
	setAvailability: (valueId: string, geoValueIds: string[]) =>
		apiClient
			.put<{ data: AvailabilityDTO[] }>(`/values/${valueId}/availability`, { geoValueIds })
			.then((r) => unwrap(r.data)),

	// Facet drilldown — the active-values primitive. objectType/countBy/requireAxes optional.
	facet: (
		axisCode: string,
		filter: FacetFilter,
		opts?: { objectType?: string; countBy?: string; requireAxes?: string[] },
	) =>
		apiClient
			.post<{ data: FacetValue[] }>('/facet', {
				axisCode,
				filter,
				objectType: opts?.objectType,
				countBy: opts?.countBy,
				requireAxes: opts?.requireAxes,
			})
			.then((r) => unwrap(r.data)),

	// Batch facet drilldown — active values for MANY axes under one accumulated
	// filter, in one call (skip-self semantics server-side). Open to any
	// authenticated user, unlike the admin-gated single-axis `facet` above.
	facetBatch: (
		filter: FacetFilter,
		axes: string[],
		opts?: { objectType?: string; requireAxes?: string[]; candidateObjectIds?: string[] },
	): Promise<FacetBatchResult> =>
		apiClient
			.post<FacetBatchResult>('/facet/batch', {
				filter,
				axes,
				objectType: opts?.objectType,
				requireAxes: opts?.requireAxes,
				candidateObjectIds: opts?.candidateObjectIds,
			})
			.then((r) => r.data),

	// Catalog reads (for the product leaf of the drilldown)
	listBrands: () => apiClient.get<{ data: BrandDTO[] }>('/brands').then((r) => unwrap(r.data)),
	listProducts: () => apiClient.get<{ data: ProductDTO[] }>('/products').then((r) => unwrap(r.data)),
	getProduct: (productId: string) => apiClient.get<ProductDTO>(`/products/${productId}`).then((r) => r.data),
	getProductPage: (productId: string) =>
		apiClient.get<Record<string, unknown>>(`/products/${productId}/page`).then((r) => r.data),

	// ─── Classification writes: classify · replace · remove (+ bulk) ───────────

	// Add ONE new assertion to an object. The tuple id is allocated server-side
	// and returned — never client-computed, which is what closes the race between
	// two concurrent adds picking the same "next" ordinal.
	classify: (objectType: string, objectId: string, tuple: AxisSelection) =>
		apiClient
			.post<{ data: { tupleId: number } }>('/classify', { objectType, objectId, tuple })
			.then((r) => unwrap(r.data).tupleId),
	// Bulk tag write across many objects, for pipelines (upload, work-order
	// seeding). Additive and idempotent, so a retried batch converges.
	classifyBatch: (tuples: ClassifyTuple[]) => apiClient.post<void>('/classify/batch', { tuples }).then((r) => r.data),
	// Delete ONE assertion, leaving the object's others intact. Idempotent.
	removeTuple: (objectType: string, objectId: string, tupleId: number) =>
		apiClient.post<void>('/classify/remove-tuple', { objectType, objectId, tupleId }).then((r) => r.data),
	// Set an object_attribute value-set (free-text/list attributes like `order_code`,
	// `file_type` — NOT dimension values, which go through classify). Replace-set.
	setAttribute: (objectType: string, objectId: string, axisCode: string, valueCodes: string[]) =>
		apiClient.post<void>('/set-attribute', { objectType, objectId, axisCode, valueCodes }).then((r) => r.data),
	// Browse by a DIMENSION selection (matched against an object's tags) plus an
	// optional ATTRIBUTE selection (matched against its object_attribute values,
	// e.g. `order_code`).
	search: (objectType: string, filters: AxisSelection, attributeFilters: AxisSelection = {}) =>
		apiClient
			.post<{ data: ObjectRef[] }>('/search', { objectType, filters, attributeFilters })
			.then((r) => unwrap(r.data)),

	/**
	 * Explain why ONE object does or does not match a context — the `matching`
	 * question with its reasons attached: which of the object's assertions matched,
	 * and per axis what decided it. A diagnostic; admin-only server-side.
	 */
	explainMatch: (objectType: string, objectId: string, context: AxisSelection, mode?: MatchMode) =>
		apiClient
			.post<{ data: CoverageExplanation }>('/matching/explain', { objectType, objectId, context, mode })
			.then((r) => unwrap(r.data)),
	// Batch-read the resolved DIMENSION classification tags of objects (codes +
	// labels). Does NOT include object_attribute values (e.g. order_code) — those
	// are a separate read. Used to hydrate an edit form's classification picker.
	getObjectTags: (objectType: string, objectIds: string[]) =>
		apiClient.post<{ data: ObjectTags[] }>('/tags', { objectType, objectIds }).then((r) => unwrap(r.data)),
	// Batch-read the object_attribute values (e.g. order_code) of objects on the
	// requested attribute axes. Attributes come back as CODES keyed by axisCode —
	// there is no label resolution for free-text attributes (the code IS the value,
	// e.g. order_code → "WO-001001"). Complements getObjectTags for the full descriptor set.
	getObjectAttributes: (objectType: string, objectIds: string[], axisCodes: string[]) =>
		apiClient
			.post<{ data: { objectId: string; attributes: Record<string, string[]> }[] }>('/object-attributes', {
				objectType,
				objectIds,
				axisCodes,
			})
			.then((r) => unwrap(r.data)),
}
